import { useState, type ChangeEvent } from "react";
import { api } from "../../api/client";
import type { StaffSnapshot } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { ColorField } from "./Storefront";

/**
 * Рекламные баннеры витрины.
 *
 * Отдельный экран от акций, потому что это разные обещания. Акция считает
 * корзину: заведи её — и цена изменится сама. Баннер не считает ничего, он
 * только показывает. Раньше единственным способом что-то объявить гостю была
 * акция, и владельцу приходилось заводить пустое правило ради строки текста.
 */

type Banner = StaffSnapshot["banners"][number];
type Placement = Banner["placement"];
type Action = Banner["action"];

const PLACEMENTS: { id: Placement; title: string; hint: string; anchored: boolean }[] = [
  {
    id: "top",
    title: "Полоса под шапкой",
    hint: "Первое, что видит гость после номера лавочки. Несколько таких баннеров листаются пальцем.",
    anchored: false
  },
  {
    id: "section",
    title: "Перед разделом меню",
    hint: "Широкая полоса над блюдами раздела. Ловит гостя на переходе от одного блока меню к другому.",
    anchored: true
  },
  {
    id: "grid",
    title: "В сетке блюд",
    hint: "Квадратная плитка ростом с карточку блюда. Говорит с тем, кто уже выбирает еду в этом разделе.",
    anchored: true
  }
];

const ACTIONS: { id: Action; title: string }[] = [
  { id: "none", title: "Ничего — это объявление" },
  { id: "product", title: "Открыть карточку товара" },
  { id: "category", title: "Прокрутить к разделу меню" }
];

/** Цвета по умолчанию совпадают с акцентом светлой витрины. */
const FALLBACK = { bg: "#ff6a1a", ink: "#ffffff" };

function placementOf(banner: Banner) {
  return PLACEMENTS.find(item => item.id === banner.placement);
}

export function BannersPage() {
  const { snapshot } = useAppState();
  const { run } = useToasts();
  const [editing, setEditing] = useState<Banner | null | undefined>(undefined);

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  async function toggleActive(banner: Banner, active: boolean) {
    await run(() => api.patch(`/api/admin/banners/${banner.id}`, { active }));
  }

  async function remove(banner: Banner) {
    if (!confirm(`Удалить баннер «${banner.title}»?`)) return;
    await run(() => api.delete(`/api/admin/banners/${banner.id}`), "Баннер удалён");
  }

  const categoryTitle = (slug: string | null) =>
    snapshot.categories.find(category => category.id === slug)?.title ?? slug ?? "—";

  const targetTitle = (banner: Banner) => {
    if (banner.action === "product") {
      return snapshot.products.find(product => product.id === banner.targetSlug)?.title ?? banner.targetSlug ?? "—";
    }
    if (banner.action === "category") return categoryTitle(banner.targetSlug);
    return "—";
  };

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Витрина</p>
          <h1>Баннеры</h1>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setEditing(null)}>
          + Баннер
        </button>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Что висит на витрине</h2>
            <p>
              Гость открывает меню на телефоне, стоя на улице. Баннер здесь — это картинка с подписью,
              а не отдельная страница: всё, что он умеет, — привести к товару или к разделу меню.
            </p>
          </div>
        </header>

        {snapshot.banners.length === 0 ? (
          <Empty>Баннеров пока нет.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Баннер</th>
                  <th>Место</th>
                  <th>Раздел</th>
                  <th>По нажатию</th>
                  <th>Порядок</th>
                  <th>Показывается</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {snapshot.banners.map(banner => (
                  <tr key={banner.id}>
                    <td>
                      <strong>{banner.title}</strong>
                      {banner.text ? (
                        <>
                          <br />
                          <small className="muted">{banner.text}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="tiny">{placementOf(banner)?.title ?? banner.placement}</td>
                    <td className="tiny">
                      {placementOf(banner)?.anchored ? categoryTitle(banner.anchorSlug) : "—"}
                    </td>
                    <td className="tiny">
                      {ACTIONS.find(action => action.id === banner.action)?.title}
                      {banner.action === "none" ? null : (
                        <>
                          <br />
                          <span className="muted">{targetTitle(banner)}</span>
                        </>
                      )}
                    </td>
                    <td className="num">{banner.sort}</td>
                    <td>
                      <Toggle
                        checked={banner.active}
                        label={`Баннер «${banner.title}» показывается`}
                        onChange={next => void toggleActive(banner, next)}
                      />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-sm" onClick={() => setEditing(banner)}>
                          Изменить
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => void remove(banner)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing !== undefined ? (
        <BannerForm banner={editing} snapshot={snapshot} onClose={() => setEditing(undefined)} />
      ) : null}
    </>
  );
}

function BannerForm({
  banner,
  snapshot,
  onClose
}: {
  banner: Banner | null;
  snapshot: StaffSnapshot;
  onClose: () => void;
}) {
  const { run, notify } = useToasts();
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    title: banner?.title ?? "",
    text: banner?.text ?? "",
    imageUrl: banner?.imageUrl ?? null,
    colorBg: banner?.colors.bg ?? null,
    colorInk: banner?.colors.ink ?? null,
    placement: banner?.placement ?? ("top" as Placement),
    anchorSlug: banner?.anchorSlug ?? "",
    action: banner?.action ?? ("none" as Action),
    targetSlug: banner?.targetSlug ?? "",
    sort: String(banner?.sort ?? 0)
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  const meta = PLACEMENTS.find(item => item.id === form.placement)!;

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await api.upload<{ url: string }>("/api/admin/catalog/images", file);
      update("imageUrl", result.url);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить картинку", "error");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function save() {
    const body = {
      title: form.title.trim(),
      text: form.text.trim(),
      imageUrl: form.imageUrl,
      colorBg: form.colorBg,
      colorInk: form.colorInk,
      placement: form.placement,
      // Привязка к разделу имеет смысл только там, где баннер стоит внутри
      // меню. Для полосы под шапкой она стирается, а не остаётся висеть
      // невидимым хвостом от прошлого выбора.
      anchorSlug: meta.anchored ? form.anchorSlug || null : null,
      action: form.action,
      targetSlug: form.action === "none" ? null : form.targetSlug || null,
      sort: Number(form.sort) || 0
    };

    const ok = await run(
      () => (banner ? api.patch(`/api/admin/banners/${banner.id}`, body) : api.post("/api/admin/banners", body)),
      banner ? "Баннер сохранён" : "Баннер создан"
    );

    if (ok) onClose();
  }

  const targetMissing = form.action !== "none" && !form.targetSlug;
  const anchorMissing = meta.anchored && !form.anchorSlug;

  return (
    <Modal
      title={banner ? `Баннер «${banner.title}»` : "Новый баннер"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={!form.title.trim() || targetMissing || anchorMissing}
          >
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Заголовок</span>
        <input
          autoFocus
          value={form.title}
          onChange={event => update("title", event.target.value)}
          placeholder="Кофе к завтраку — 99 ₽"
          maxLength={120}
        />
      </label>

      <label className="field">
        <span>Подпись</span>
        <input
          value={form.text}
          onChange={event => update("text", event.target.value)}
          placeholder="До полудня, каждый день"
          maxLength={300}
        />
      </label>

      <label className="field">
        <span>Где стоит</span>
        <select value={form.placement} onChange={event => update("placement", event.target.value as Placement)}>
          {PLACEMENTS.map(item => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
      </label>
      <p className="tiny muted">{meta.hint}</p>

      {meta.anchored ? (
        <label className="field">
          <span>Раздел меню</span>
          <select value={form.anchorSlug} onChange={event => update("anchorSlug", event.target.value)}>
            <option value="">— выберите —</option>
            {snapshot.categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="field">
        <span>{form.placement === "grid" ? "Место в сетке" : "Порядок показа"}</span>
        <input inputMode="numeric" value={form.sort} onChange={event => update("sort", event.target.value)} />
        <small className="tiny muted">
          {form.placement === "grid"
            ? "Номер ячейки среди блюд раздела: 0 — перед первым блюдом, 2 — третьей плиткой. Номер больше, чем блюд в разделе, ставит баннер в конец."
            : "Меньшее число показывается раньше."}
        </small>
      </label>

      <label className="field">
        <span>По нажатию</span>
        <select value={form.action} onChange={event => update("action", event.target.value as Action)}>
          {ACTIONS.map(action => (
            <option key={action.id} value={action.id}>
              {action.title}
            </option>
          ))}
        </select>
        {/* Гость пришёл по QR с лавочки, чтобы заказать. Ссылки наружу здесь
            нет намеренно: увести его на сторонний сайт посреди выбора еды —
            значит потерять заказ, который уже почти состоялся. */}
        <small className="tiny muted">Баннер ведёт только внутрь меню — гость не уходит со страницы заказа.</small>
      </label>

      {form.action === "product" ? (
        <label className="field">
          <span>Товар</span>
          <select value={form.targetSlug} onChange={event => update("targetSlug", event.target.value)}>
            <option value="">— выберите —</option>
            {snapshot.products.map(product => (
              <option key={product.id} value={product.id}>
                {product.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {form.action === "category" ? (
        <label className="field">
          <span>Раздел, к которому вести</span>
          <select value={form.targetSlug} onChange={event => update("targetSlug", event.target.value)}>
            <option value="">— выберите —</option>
            {snapshot.categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="field-row">
        <label className="field">
          <span>Картинка</span>
          <input type="file" accept="image/*" onChange={event => void upload(event)} disabled={uploading} />
        </label>

        {form.imageUrl ? (
          <div className="row-actions">
            <img src={form.imageUrl} alt="" className="storefront-hero" />
            <button type="button" className="btn btn-sm" onClick={() => update("imageUrl", null)}>
              Убрать картинку
            </button>
          </div>
        ) : (
          /* Баннер без картинки — рабочий вид, а не заготовка: снять фотографию
             к завтрашней акции успевают не всегда, а сказать про неё надо сегодня. */
          <p className="tiny muted">Без картинки баннер рисуется заливкой и подписью.</p>
        )}
      </div>

      <div className="field-row">
        <ColorField
          label="Заливка"
          value={form.colorBg}
          fallback={FALLBACK.bg}
          onChange={value => update("colorBg", value)}
        />
        <ColorField
          label="Текст"
          value={form.colorInk}
          fallback={FALLBACK.ink}
          onChange={value => update("colorInk", value)}
        />
      </div>

      {/* Предпросмотр показывает ровно то сочетание, которое сейчас в полях:
          подобрать цвета вслепую и увидеть результат у гостя — плохой обмен. */}
      <div
        className="banner-preview"
        style={{
          background: form.colorBg ?? FALLBACK.bg,
          color: form.colorInk ?? FALLBACK.ink,
          backgroundImage: form.imageUrl ? `url(${form.imageUrl})` : undefined
        }}
      >
        <b>{form.title || "Заголовок баннера"}</b>
        {form.text ? <span>{form.text}</span> : null}
      </div>
    </Modal>
  );
}
